import { useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import {
  certificateCodeGroups,
  cryptoRandomIndex,
  mintCertificateCode,
  normalizePrintedCertificateCode,
} from '@yourstore/domain';
import { useActions, useLive, useRuntime } from './context';
import { formatMoney, isoDayIn } from '../mirror/time';
import { centsFromInput, inputFromCents } from '../money/input';
import { certificateLiability, certificateState } from '../money/certificates';
import { ActionGlyph } from './brand';
import { useActionIcon } from './useRemote';
import { useLocale, useT } from '../messages';
import type { T } from '../messages';
import { GiftRequestsPanel } from './GiftRequests';
import type { SellPrefill } from './GiftRequests';
import { tillTendersFor } from './Checkout';
import { ClientPicker } from './ClientPicker';
import type { PickedClient } from './ClientPicker';
import type { Certificate, RemainderKind, Service } from '../wire';

/**
 * Gift cards — the platform's certificates (module `certificates`, spec §7.3),
 * on a tab of their own (owner's decision 1 of 2026-09-23).
 *
 * **The shape is her old app's.** Love Spa ran a year of gift cards on the
 * LoveSpa iPad, and its screen is the one she reaches for: a line that says how
 * many are outstanding and what they are worth, a search box that finds a card
 * by its code or by anybody's name on it, four filters with *Outstanding*
 * first, a list she can read across a counter, and a button that opens the sale
 * as a sheet. What it deliberately does not bring across is the old form's
 * *Cash this certificate* and *Delete* — see the next two paragraphs.
 *
 * Three operations here and one deliberately elsewhere: **redemption happens
 * only in a checkout**. There is no `POST /certificates/{id}/redeem` on the
 * server and there is no button for it here, for the reason
 * `routes/certificates.ts` states — a certificate spent outside a settlement is
 * money leaving the ledger with no document behind it, and the §7.4 arithmetic
 * would have nothing to close against. The empty state says so out loud, because
 * the owner's first question on this screen is going to be "where do I redeem
 * one". Nor is there a delete: a card is money that moved on `issued_on`, and a
 * refund (which voids what is left) is the only way back.
 *
 * **Issuing is a money event, not a sale document.** §7.4 rule 1: the
 * certificate row *is* the record, `issued_on` is the date of money, and the
 * revenue report folds it from there. Giving it a settlement as well would
 * recognize the same money twice — once on `issued_on` and once as a `cash`
 * tender — and the cutover gate would fail by the price of every certificate
 * ever sold.
 *
 * **…and it records how it was paid** (owner's decision 2, migration 0070). The
 * sheet asks with the till's own tender buttons — `tillTendersFor`, the same
 * source `Checkout` draws from, so a shop that dropped cards has no card button
 * here either — and will not issue until one is chosen: a gift card sale always
 * has a payment, and a row that says *how* is the difference between a Venmo
 * she can find at the end of the month and $170 that arrived somehow. The
 * server stores it on the card (`paid_with`), the report folds it per tender
 * beside the counter's (`certificate_by_tender`), and Today's running total adds
 * the two. The server does not *require* it — an entry queued before this
 * bundle carries none — but this screen never sends a card without one.
 *
 * **A card may name a length** (migration 0054, the owner's ruling 2). When the
 * chosen service is sold in more than one (`service.options`), a length select
 * appears, lands on the default, and the amount box is **prefilled with that
 * length's catalog price and labelled as the catalog's**. The prefill is a
 * courtesy, not a claim: if the box still says the catalog's number nothing is
 * sent and the server snapshots the option's price itself; if the owner types
 * another amount, that is sent, as a typed value always has been on this
 * trusted surface. Either way the `option_id` rides along, and the list prints
 * "Custom Deluxe Facial · 90 minutes" rather than "a facial". The snapshot is
 * the price the card was *sold* at, and it stays that price (decision 3).
 *
 * **State is recomputed here, never read off the row.** See
 * `money/certificates.ts`: expiry is derived, and a mirrored `state` fetched
 * last Tuesday would tell the counter a dead card is live. The filters use the
 * same derivation, so "Outstanding" is outstanding *today*.
 */

type ServiceOption = Service['options'][number];

/** The live lengths of a service, in the owner's order. `[]` for one sold one way. */
function lengthsOf(service: Service | undefined): ServiceOption[] {
  return (service?.options ?? [])
    .filter((o) => o.active)
    .sort((a, b) => a.sort_order - b.sort_order || a.option_id.localeCompare(b.option_id));
}

/** `defaultItemOption`'s rule: the marked default, else the first in order. */
function defaultLength(options: readonly ServiceOption[]): ServiceOption | undefined {
  return options.find((o) => o.is_default) ?? options[0];
}

/**
 * Her old screen's four filters, in its order, with its default first.
 * `open` is the derived word for what she calls *outstanding*; a refunded card
 * (`voided`) is under *All* only, because it is neither owed nor spent.
 */
const FILTERS = ['open', 'redeemed', 'expired', 'all'] as const;
type Filter = (typeof FILTERS)[number];

function filterLabel(t: T, filter: Filter): string {
  switch (filter) {
    case 'open':
      return t('giftcards.filterOutstanding');
    case 'redeemed':
      return t('giftcards.filterRedeemed');
    case 'expired':
      return t('giftcards.filterExpired');
    case 'all':
      return t('giftcards.filterAll');
  }
}

/** "Custom Deluxe Facial · 90 minutes", "Swedish Massage · 60 min", or null for an amount. */
function serviceWords(certificate: Certificate, services: readonly Service[], t: T): string | null {
  if (certificate.item_name === null) return null;
  if (certificate.option_name !== null) {
    return t('certificates.namedLength', {
      name: certificate.item_name,
      length: certificate.option_name,
    });
  }
  // A card for an item sold one way: its length is the catalog's, when the
  // mirror still has the item. The name is the card's own (live catalog
  // content, resolved by the server), so a retired item still reads by name.
  const service = services.find((s) => s.service_id === certificate.item_id);
  return service === undefined
    ? certificate.item_name
    : t('book.serviceOption', { name: certificate.item_name, minutes: service.duration_min });
}

/**
 * Does the search box match this card? Code, recipient, buyer and service —
 * her old screen's four — case-insensitive and by substring, because the
 * question at the desk is "the one for her sister", not an exact field.
 *
 * The code also matches **without its dashes**: somebody reading "ABCD EFGH"
 * off a card types it with a space, or with nothing, and either is the card.
 */
function matches(certificate: Certificate, needle: string, service: string | null): boolean {
  if (needle === '') return true;
  const bare = (text: string): string => text.toLowerCase().replace(/[^a-z0-9]/g, '');
  const bareNeedle = bare(needle);
  if (bareNeedle.length >= 3 && bare(certificate.code).includes(bareNeedle)) return true;
  return [certificate.code, certificate.recipient_name, certificate.purchaser_name, service]
    .filter((field): field is string => field !== null && field !== '')
    .some((field) => field.toLowerCase().includes(needle));
}

export function GiftCards(): ReactNode {
  const runtime = useRuntime();
  const action = useActionIcon();
  const t = useT();
  const locale = useLocale();

  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const currency = session?.tenant.currency ?? 'USD';
  const today = isoDayIn(timezone, runtime.now());

  const certificates = useLive(() => runtime.mirror.certificates(), []);
  const services = useLive(() => runtime.mirror.services(), []);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('open');
  /** `null` is closed; a value is open, prefilled from a gift request or empty. */
  const [selling, setSelling] = useState<SellPrefill | 'blank' | null>(null);

  const liability = certificateLiability(certificates, today);
  const needle = search.trim().toLowerCase();
  const shown = certificates.filter((certificate) => {
    if (filter !== 'all' && certificateState(certificate, today) !== filter) return false;
    return matches(certificate, needle, serviceWords(certificate, services, t));
  });

  return (
    <section className="view view--giftcards">
      <div className="view__header">
        <h1>{t('giftcards.heading')}</h1>
        <button
          type="button"
          className={`button button--primary${action.icon === 'heart' ? ' button--heart' : ''}`}
          onClick={() => setSelling('blank')}
        >
          <ActionGlyph icon={action.icon} />
          {/* The heart is the plus on a heart tenant; the text never says both. */}
          {action.icon === 'heart' ? null : '+ '}
          {t('giftcards.new')}
        </button>
      </div>

      {/* Her old screen's subtitle: the count and the money, in one breath.
          `outstandingCents` is what is actually left on open cards — the number
          she owes in treatments. */}
      <p className="giftcards__summary">
        {t('giftcards.summary', {
          count: liability.open.length,
          amount: formatMoney(liability.outstandingCents, currency, locale),
        })}
      </p>

      {/* The promises the desk has not settled yet (owner's ruling 1). Above
          the list rather than inside it, because the two must never be read as
          one thing: an unpaid ask is not a liability, and spec 7.4 rule 1 is
          why. "Sell this certificate" opens the sheet on the ask. */}
      <GiftRequestsPanel currency={currency} onSell={(prefill) => setSelling(prefill)} />

      <div className="view__header">
        <input
          className="search"
          aria-label={t('giftcards.searchLabel')}
          placeholder={t('giftcards.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="cal__modes" role="group" aria-label={t('giftcards.filterLabel')}>
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={`button${filter === f ? ' is-current' : ''}`}
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
            >
              {filterLabel(t, f)}
            </button>
          ))}
        </div>
      </div>

      <div className="panel">
        <ul className="certificate-list">
          {shown.map((certificate) => {
            const state = certificateState(certificate, today);
            const what = serviceWords(certificate, services, t) ?? t('giftcards.giftAmount');
            const people: string[] = [];
            if (certificate.recipient_name !== '') {
              people.push(t('giftcards.forWhom', { name: certificate.recipient_name }));
            }
            if (certificate.purchaser_name !== null && certificate.purchaser_name !== '') {
              people.push(t('giftcards.boughtBy', { name: certificate.purchaser_name }));
            }
            // `?? null`, because a row mirrored before 0070 is read back raw
            // (the mirror stores what was parsed that day) and has no field at
            // all until the next hydrate rewrites it — offline, that is a while.
            const paidWith = certificate.paid_with ?? null;
            const paidLabel = certificate.paid_with_label ?? null;
            const facts = [
              ...people,
              t('giftcards.issued', { date: certificate.issued_on }),
              certificate.expires_on === null
                ? t('giftcards.neverExpires')
                : state === 'expired'
                  ? t('giftcards.expired', { date: certificate.expires_on })
                  : t('giftcards.expires', { date: certificate.expires_on }),
              ...(paidWith === null
                ? []
                : [
                    t('giftcards.paidWith', {
                      tender:
                        paidWith === 'other' && paidLabel !== null
                          ? paidLabel
                          : t(`checkout.tender.${paidWith}`),
                    }),
                  ]),
            ];
            return (
              <li key={certificate.certificate_id} className={`is-${state}`}>
                <div>
                  {/* The code first and in monospace (0074's complaint: the card
                    is tracked by it) — one text run, so it reads, searches and
                    copies exactly as stored. */}
                  <strong className="certificate-code">{certificate.code}</strong>
                  {/* `item_name` is the tenant's own words for what the card
                    buys, and `option_name` which length of it (0054). */}
                  <span>
                    {t('giftcards.whatAndValue', {
                      what,
                      value: formatMoney(certificate.value_cents, currency, locale),
                    })}
                  </span>
                  <small>{facts.join(' · ')}</small>
                </div>
                <div className="row-actions">
                  {/* The derived `state` word is the one `money/certificates.ts`
                    computes to match the server's own vocabulary, and is shown
                    as it comes. */}
                  <span className="badge">{state}</span>
                  <span>
                    {t('certificates.remainingOf', {
                      remaining: formatMoney(certificate.remaining_cents, currency, locale),
                      value: formatMoney(certificate.value_cents, currency, locale),
                    })}
                  </span>
                </div>
              </li>
            );
          })}
          {/* LoveSpa's own empty state — "No outstanding certificates — issue one
            with the heart button" — is the line `docs/lovespa-personality.md`
            names as the reason copy has to follow the icon. With no cards at
            all the sentence also says where redemption lives; with cards and a
            filter or search that hides them, it says only that. */}
          {certificates.length === 0 ? (
            <li className="muted">
              {t('certificates.empty', {
                where:
                  action.icon === 'heart'
                    ? t('certificates.emptyWithButton', { button: action.buttonName })
                    : t('certificates.emptyHere'),
              })}
            </li>
          ) : shown.length === 0 ? (
            <li className="muted">
              {needle !== '' ? t('giftcards.noMatch') : t('giftcards.noneInFilter')}
            </li>
          ) : null}
        </ul>
      </div>

      {/*
        Both numbers, never one, under the list. They differ exactly when a
        partial redemption is in play, and the cutover gate compares the face
        value — quietly showing whichever is smaller would be a reconciliation
        nobody could explain (`certificateLiabilityRoute`).
      */}
      <dl className="facts">
        <dt>{t('certificates.faceValue')}</dt>
        <dd>{formatMoney(liability.faceValueCents, currency, locale)}</dd>
        <dt>{t('certificates.expiredUnspent')}</dt>
        <dd>{formatMoney(liability.expiredCents, currency, locale)}</dd>
      </dl>

      {selling === null ? null : (
        <IssueGiftCardSheet
          currency={currency}
          prefill={selling === 'blank' ? null : selling}
          onClose={() => setSelling(null)}
        />
      )}
    </section>
  );
}

/**
 * The sale, as a sheet — the app's `.sheet`/`.sheet__panel` pattern, like the
 * new-client sheet beside it.
 *
 * Everything the old inline editor did (service and length prefilled at the
 * catalog's price, a typed amount, expiry), the question it never asked —
 * **how was it paid?** — and, since the owner's two complaints of 2026-09-30,
 * the two things a gift card cannot be tracked without:
 *
 *  1. **A code.** *"There is no code on the gift card — that's imperative."*
 *     It is minted **here**, with the server's own alphabet and length
 *     (`mintCertificateCode` in `@yourstore/domain`, from Web Crypto), and
 *     sent as `code` — so the confirmation can show it large enough to read
 *     aloud the moment she taps Issue, with or without signal, and it is the
 *     code the server stores (it validates, uppercases and refuses a
 *     duplicate). Or it is **the number printed on a physical card** she
 *     bought in advance, typed in. Her old LoveSpa app numbered cards
 *     `GC-` + four digits from `Math.random()`; those imported codes keep
 *     working, and a new code is the unguessable twelve.
 *  2. **Who holds it, as a client.** *"A client should be able to gift another
 *     client — no free typing."* Recipient and purchaser are the same
 *     `ClientPicker`; the recipient is required (a card is tracked by who
 *     holds it), the purchaser may be a walk-in. A client added inline is
 *     queued as `client.create` with its own minted id **before** the card,
 *     and the outbox lands them in that order.
 *
 * After Issue the sheet does not close: it turns into the card — code,
 * value, for, from, expiry, and whether it has reached the books yet — with a
 * Copy button, and Done.
 */
function IssueGiftCardSheet({
  currency,
  prefill,
  onClose,
}: {
  currency: string;
  /** From "Sell this certificate" on a gift request, or `null` for a blank sale. */
  prefill: SellPrefill | null;
  onClose: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const action = useActionIcon();
  const t = useT();
  const locale = useLocale();

  const session = useLive(() => runtime.mirror.session(), null);
  const services = useLive(() => runtime.mirror.services(), []);
  const certificates = useLive(() => runtime.mirror.certificates(), []);
  // The till's buttons, from the till's own source: `session.till.tenders`
  // with Checkout's fallback. One list of "how this shop is paid", never two.
  const tenders = tillTendersFor(session);

  /**
   * The ask's service and length, resolved once the catalog has arrived. The
   * initial state cannot do it — `services` is a live read that resolves a
   * paint after the sheet mounts — so a prefill naming a service is applied
   * the first time the catalog is there, and never again (the owner may change
   * her mind about the length).
   */
  const [valueText, setValueText] = useState(
    prefill !== null && prefill.itemId === null && prefill.amountCents !== null
      ? inputFromCents(prefill.amountCents)
      : '',
  );
  const [itemId, setItemId] = useState(prefill?.itemId ?? '');
  const [optionId, setOptionId] = useState(prefill?.optionId ?? '');
  const [prefillApplied, setPrefillApplied] = useState(prefill === null || prefill.itemId === null);
  const [recipient, setRecipient] = useState<PickedClient | null>(null);
  const [purchaser, setPurchaser] = useState<PickedClient | null>(null);
  const [expiresOn, setExpiresOn] = useState('');
  const [tender, setTender] = useState<RemainderKind | null>(null);
  const [tenderLabel, setTenderLabel] = useState('');
  /** `null` mints a code at Issue; a string is the number printed on the card. */
  const [printed, setPrinted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Set once the card is queued: the sheet becomes the card. */
  const [issued, setIssued] = useState<IssuedCard | null>(null);

  const valueCents = centsFromInput(valueText);

  const chosen = services.find((s) => s.service_id === itemId);
  const lengths = lengthsOf(chosen);
  const length = lengths.find((o) => o.option_id === optionId);
  const printedCode = printed === null ? null : normalizePrintedCertificateCode(printed);

  /** Choose a service: a length lands on its default and the box on that price. */
  const chooseService = (serviceId: string, preferredOptionId = ''): void => {
    setItemId(serviceId);
    const options = lengthsOf(services.find((s) => s.service_id === serviceId));
    const picked = options.find((o) => o.option_id === preferredOptionId) ?? defaultLength(options);
    setOptionId(picked?.option_id ?? '');
    setValueText(picked === undefined ? '' : inputFromCents(picked.price_cents));
  };

  if (!prefillApplied && prefill !== null && prefill.itemId !== null && chosen !== undefined) {
    // Render-phase state adjustment (React's documented pattern for "derive
    // once from a prop when data arrives"), guarded so it runs exactly once.
    setPrefillApplied(true);
    chooseService(prefill.itemId, prefill.optionId ?? '');
  }

  const chooseLength = (id: string): void => {
    setOptionId(id);
    const picked = lengths.find((o) => o.option_id === id);
    if (picked !== undefined) setValueText(inputFromCents(picked.price_cents));
  };

  const issue = async (): Promise<void> => {
    // Either a value or a service, exactly as `IssueCertificateBody` refines it.
    // The service form snapshots the item's list price on the server, which is
    // why no price is sent with it: "one Swedish massage" has to mean what a
    // Swedish massage cost the day it was bought. A length (0054) is the same
    // rule one step narrower — the server takes the option's price — and the
    // prefilled box is sent only when the owner changed it.
    if (itemId === '' && (valueCents === null || valueCents <= 0)) {
      return setError(t('certificates.needAmount'));
    }
    // A card is tracked by who holds it (0074): no holder, no card.
    if (recipient === null) return setError(t('giftcards.needRecipient'));
    // A gift card sale always has a payment (owner's decision 2). Asked here
    // rather than defaulted: a pre-chosen `cash` would be the device answering
    // the one question this form exists to put to her.
    if (tender === null) return setError(t('giftcards.needTender'));
    if (printed !== null) {
      if (printedCode === null) return setError(t('giftcards.codePrintedInvalid'));
      // The mirror knows every card this device has seen: say so now, in her
      // words, rather than as a refused task after the drain. The server's
      // unique index is still the last word (a card sold on another device a
      // minute ago is not in this mirror yet).
      const clash = certificates.find((c) => c.code.toUpperCase() === printedCode);
      if (clash !== undefined) {
        return setError(
          t('giftcards.codeTaken', {
            name:
              clash.recipient_name === ''
                ? ''
                : t('giftcards.codeTakenFor', { name: clash.recipient_name }),
          }),
        );
      }
    }

    const typedOverCatalog =
      length !== undefined &&
      valueCents !== null &&
      valueCents > 0 &&
      valueCents !== length.price_cents;
    const name = chosen?.name ?? t('certificates.aService');
    const label = tender === 'other' ? tenderLabel.trim() : '';
    const what =
      itemId === ''
        ? null
        : length === undefined
          ? name
          : t('certificates.namedLength', { name, length: length.name });
    // What the card is worth as far as this device can say: the typed amount,
    // or the catalog's price the server will snapshot. Shown on the
    // confirmation until the server's own row arrives in the mirror.
    const shownValue =
      itemId === '' || typedOverCatalog
        ? (valueCents ?? 0)
        : (length?.price_cents ?? chosen?.price_cents ?? 0);
    const code = printedCode ?? mintCertificateCode(cryptoRandomIndex);
    const certificateId = `ct_${ulid()}`;

    setBusy(true);
    try {
      // **The new clients first**, awaited one at a time: `enqueue` hands out
      // the next `seq`, and the drain replays in `seq` order, so a client
      // queued here always lands before the card that names her — in a tunnel
      // as much as at four bars.
      const queued = async (who: PickedClient): Promise<PickedClient> => {
        if (who.draft === undefined) return who;
        await actions.createClient({
          id: who.clientId,
          name: who.draft.name,
          identities: who.draft.identities,
        });
        // Queued: from here on she is a client with an id, not a draft, so a
        // "go back and fix it" cannot queue her a second time.
        return { ...who, draft: undefined };
      };
      const holder = await queued(recipient);
      setRecipient(holder);
      const buyer = purchaser === null ? null : await queued(purchaser);
      setPurchaser(buyer);

      const entry = await actions.issueCertificate({
        id: certificateId,
        code,
        ...(itemId === '' ? { valueCents: valueCents as number } : { itemId }),
        ...(length === undefined ? {} : { optionId: length.option_id }),
        ...(typedOverCatalog ? { valueCents: valueCents as number } : {}),
        recipientClientId: holder.clientId,
        recipientName: holder.name,
        ...(buyer === null ? {} : { purchaserClientId: buyer.clientId }),
        ...(expiresOn === '' ? {} : { expiresOn }),
        tender,
        ...(label === '' ? {} : { tenderLabel: label }),
        // Frozen at the moment of the tap, like every `describe` — see the
        // doc comment on `createActions`.
        what:
          what === null
            ? t('certificates.issuingAmount', { amount: formatMoney(shownValue, currency, locale) })
            : t('certificates.issuingService', { name: what }),
      });
      setIssued({
        entryId: entry.id,
        certificateId,
        code,
        what,
        valueCents: shownValue,
        recipientName: holder.name,
        purchaserName: buyer?.name ?? null,
        expiresOn: expiresOn === '' ? null : expiresOn,
        paid: tender === 'other' && label !== '' ? label : t(`checkout.tender.${tender}`),
      });
      setError(null);
    } catch (e) {
      // A support session refuses every write before it is queued; say so.
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (issued !== null) {
    return (
      <IssuedCardView
        card={issued}
        currency={currency}
        onDone={onClose}
        onFix={() => {
          // The refused card leaves the task list — she is fixing it here —
          // and the form comes back as she left it, clients already queued.
          void runtime.outbox.dismiss(issued.entryId);
          setIssued(null);
        }}
      />
    );
  }

  return (
    <div className="sheet" role="dialog" aria-label={t('giftcards.sheetLabel')}>
      <div className="sheet__panel">
        <h2>{t('giftcards.sheetLabel')}</h2>

        <label className="field">
          <span>{t('certificates.serviceLabel')}</span>
          <select
            value={itemId}
            onChange={(e) => {
              chooseService(e.target.value);
              if (e.target.value === '') setValueText('');
            }}
          >
            <option value="">{t('giftcards.amountOption')}</option>
            {services
              .filter((s) => s.active)
              .map((s) => (
                <option key={s.service_id} value={s.service_id}>
                  {t('book.serviceOption', { name: s.name, minutes: s.duration_min })}
                </option>
              ))}
          </select>
        </label>
        {lengths.length === 0 ? null : (
          <label className="field">
            <span>{t('certificates.lengthLabel')}</span>
            <select value={optionId} onChange={(e) => chooseLength(e.target.value)}>
              {lengths.map((o) => (
                <option key={o.option_id} value={o.option_id}>
                  {o.name} — {formatMoney(o.price_cents, currency, locale)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span>{t('certificates.amountLabel')}</span>
          <input
            placeholder={t('certificates.amountPlaceholder')}
            inputMode="decimal"
            value={valueText}
            onChange={(e) => {
              setValueText(e.target.value);
              // Typing an amount is choosing a value certificate — unless a
              // length is chosen, in which case the box is the catalog's prefill
              // and editing it is pricing *this* card, not abandoning the service.
              if (e.target.value !== '' && length === undefined) setItemId('');
            }}
          />
        </label>
        {length === undefined ? null : (
          <small className="muted">
            {t('certificates.catalogPrice', {
              amount: formatMoney(length.price_cents, currency, locale),
              length: length.name,
            })}
          </small>
        )}

        <div className="field field--row">
          <span>{t('giftcards.howPaid')}</span>
          {/*
            The till's buttons, in the till's markup (`Checkout.tsx` step 5):
            one tap each, only the ones this shop takes, and recorded rather
            than processed — tapping "Venmo" writes down that a Venmo arrived.
            None is pre-chosen, which is the difference from the till: there
            the remainder has a default because a visit usually closes in cash,
            and here the question is the point.
          */}
          <div className="tender-buttons" role="radiogroup" aria-label={t('giftcards.howPaid')}>
            {tenders.map((kind) => (
              <button
                key={kind}
                type="button"
                role="radio"
                aria-checked={tender === kind}
                className={`tender-button${tender === kind ? ' is-chosen' : ''}`}
                onClick={() => {
                  setTender(kind);
                  setError(null);
                }}
              >
                {t(`checkout.tender.${kind}`)}
              </button>
            ))}
          </div>
          {tender === 'other' ? (
            <input
              aria-label={t('checkout.tenderLabelLabel')}
              placeholder={t('checkout.tenderLabelPlaceholder')}
              maxLength={40}
              value={tenderLabel}
              onChange={(e) => setTenderLabel(e.target.value)}
            />
          ) : null}
        </div>

        {/* Who holds it (required) and who bought it (a walk-in is fine). */}
        <ClientPicker
          label={t('certificates.recipientLabel')}
          value={recipient}
          onChange={(value) => {
            setRecipient(value);
            setError(null);
          }}
          initialSearch={prefill?.recipientName ?? ''}
        />
        <ClientPicker
          label={t('certificates.purchaserLabel')}
          value={purchaser}
          onChange={setPurchaser}
          noneNote={t('giftcards.walkIn')}
          initialSearch={prefill?.purchaserName ?? ''}
        />

        {/* The code: made at Issue, or the number printed on a physical card. */}
        <fieldset className="field giftcard-code-field">
          <legend>{t('giftcards.codeLabel')}</legend>
          {printed === null ? (
            <>
              <p className="muted">{t('giftcards.codeMinted')}</p>
              <button type="button" className="button button--ghost" onClick={() => setPrinted('')}>
                {t('giftcards.codeUsePrinted')}
              </button>
            </>
          ) : (
            <>
              <label className="field">
                <span>{t('giftcards.codePrintedLabel')}</span>
                <input
                  className="giftcard-code-input"
                  placeholder={t('giftcards.codePrintedPlaceholder')}
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={64}
                  value={printed}
                  onChange={(e) => {
                    setPrinted(e.target.value.toUpperCase());
                    setError(null);
                  }}
                />
              </label>
              {printedCode === null || printedCode === printed.trim() ? null : (
                <small className="muted">
                  {t('giftcards.codePrintedWillBe', { code: printedCode })}
                </small>
              )}
              <button
                type="button"
                className="button button--ghost"
                onClick={() => {
                  setPrinted(null);
                  setError(null);
                }}
              >
                {t('giftcards.codeUseMinted')}
              </button>
            </>
          )}
        </fieldset>

        <label className="field">
          <span>{t('certificates.expiresLabel')}</span>
          <input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
        </label>

        {error !== null ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}
        <small className="muted">{t('certificates.footnote')}</small>

        <div className="sheet__actions">
          <button type="button" className="button button--ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className={`button button--primary${action.icon === 'heart' ? ' button--heart' : ''}`}
            disabled={busy}
            onClick={() => void issue()}
          >
            <ActionGlyph icon={action.icon} />
            {t('certificates.issue')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** What the confirmation shows — frozen at the tap, then corrected by the server's row. */
interface IssuedCard {
  /** The outbox entry, so the sheet can say whether it has landed. */
  entryId: string;
  certificateId: string;
  code: string;
  /** The service words, or `null` for an amount. */
  what: string | null;
  valueCents: number;
  recipientName: string;
  purchaserName: string | null;
  expiresOn: string | null;
  paid: string;
}

/**
 * **The card, read back to the buyer.** The code first and large — monospace,
 * in groups of four, so "A-B-C-D, E-F-G-H…" can be read over a counter or a
 * phone — then what it is worth, for whom, from whom, until when, and a line
 * that says honestly whether it has reached the books: queued on this iPad
 * (the code will not change — it was made here), saved, or refused, with the
 * server's reason in her words and a way back to the form.
 */
function IssuedCardView({
  card,
  currency,
  onDone,
  onFix,
}: {
  card: IssuedCard;
  currency: string;
  onDone: () => void;
  onFix: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const entries = useLive(() => runtime.outbox.all(), []);
  const certificates = useLive(() => runtime.mirror.certificates(), []);
  const [copied, setCopied] = useState<'copied' | 'selected' | null>(null);

  const entry = entries.find((e) => e.id === card.entryId);
  // The server's row, once the drain has written it into the mirror: its
  // value is the snapshot the server took, which is the one to read out.
  const landed = certificates.find((c) => c.certificate_id === card.certificateId);
  const refused = entry?.status === 'rejected';
  const value = landed?.value_cents ?? card.valueCents;

  const rejection = refused ? entry?.rejection : undefined;
  const detail = rejection?.detail ?? {};
  const status = refused
    ? rejection?.status === 409
      ? t('giftcards.refusedCode')
      : rejection?.status === 404 &&
          ('recipient_client_id' in detail || 'purchaser_client_id' in detail)
        ? t('giftcards.refusedClient')
        : t('giftcards.refused', { message: rejection?.message ?? '' })
    : entry?.status === 'done' || landed !== undefined
      ? t('giftcards.statusSaved')
      : t('giftcards.statusPending');

  const heading = refused ? t('giftcards.notIssuedHeading') : t('giftcards.issuedHeading');

  const copy = async (): Promise<void> => {
    try {
      if (navigator.clipboard === undefined) throw new Error('no clipboard');
      await navigator.clipboard.writeText(card.code);
      setCopied('copied');
    } catch {
      // No clipboard (an older WebView, an insecure origin, a refused
      // permission): select the code so the system menu can copy it.
      const node = document.getElementById(`code-${card.certificateId}`);
      const selection = window.getSelection();
      if (node !== null && selection !== null) {
        const range = document.createRange();
        range.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      setCopied('selected');
    }
  };

  return (
    <div className="sheet" role="dialog" aria-label={heading}>
      <div className="sheet__panel giftcard-issued">
        <h2>{heading}</h2>

        {/*
          The code as one selectable run of text (what Copy and the fallback
          selection take), drawn in groups for reading aloud. The groups are
          presentation only; the text inside is the code exactly as stored.
        */}
        <p
          id={`code-${card.certificateId}`}
          className={`giftcard-code${refused ? ' is-refused' : ''}`}
          aria-label={t('giftcards.codeAria', { code: card.code })}
        >
          {card.code.split('-').map((part, i) => (
            <span key={`${String(i)}-${part}`}>
              {i === 0 ? null : <span className="giftcard-code__dash">-</span>}
              {certificateCodeGroups(part).map((group, j) => (
                <span key={`${String(j)}-${group}`} className="giftcard-code__group">
                  {group}
                </span>
              ))}
            </span>
          ))}
        </p>

        <div className="giftcard-issued__copy">
          <button type="button" className="button" onClick={() => void copy()}>
            {t('giftcards.copy')}
          </button>
          {copied === null ? null : (
            <small className="muted" role="status">
              {copied === 'copied' ? t('giftcards.copied') : t('giftcards.copySelected')}
            </small>
          )}
        </div>

        <dl className="facts giftcard-issued__facts">
          <dt>{t('giftcards.factCard')}</dt>
          <dd>
            {t('giftcards.whatAndValue', {
              what: card.what ?? t('giftcards.giftAmount'),
              value: formatMoney(value, currency, locale),
            })}
          </dd>
          <dt>{t('giftcards.factFor')}</dt>
          <dd>{card.recipientName}</dd>
          <dt>{t('giftcards.factFrom')}</dt>
          <dd>{card.purchaserName ?? t('giftcards.walkIn')}</dd>
          <dt>{t('giftcards.factExpires')}</dt>
          <dd>{card.expiresOn ?? t('giftcards.never')}</dd>
          <dt>{t('giftcards.factPaid')}</dt>
          <dd>{card.paid}</dd>
        </dl>

        <p className={refused ? 'error' : 'muted'} role="status">
          {status}
        </p>

        <div className="sheet__actions">
          {refused ? (
            <button type="button" className="button" onClick={onFix}>
              {t('giftcards.fixIt')}
            </button>
          ) : null}
          <button type="button" className="button button--primary" onClick={onDone}>
            {t('giftcards.done')}
          </button>
        </div>
      </div>
    </div>
  );
}
